import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/synapse/Icon';
import { Modal } from '@/components/synapse/Modal';
import { useItemDetail } from '@/hooks/useInventory';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { MEASURES, MEASURE_IDS, PANEL_IDS } from '@/lib/analytics';
import type { PanelId } from '@/lib/analytics';
import type { ItemType } from '@/lib/itemTypeConfig';
import { cn } from '@/lib/utils';
import {
  GRANULARITY_LABEL_KEY,
  IssuingFilterFields,
  PeriodChips,
} from '@/features/analytics/IssuingControls';
import { IssueLogPanel } from '@/features/analytics/IssueLogPanel';
import {
  ByTypePanel,
  HeadlineTiles,
  TopItemsPanel,
  TrendPanel,
} from '@/features/analytics/IssuingPanels';
import {
  formatMeasure,
  formatShare,
  measureMetaOf,
  sumMeasure,
} from '@/features/analytics/panelData';
import { useIssuingAnalytics } from '@/features/analytics/useIssuingAnalytics';
import {
  readView,
  resolveWindow,
  writeView,
  type IssuingView,
} from '@/features/analytics/issuingUrlState';
import { calendarLabel, todayLocalIso } from '@/features/analytics/issuingWindow';

/** Above this width the whole control bar fits inline; below it, a sheet. */
const WIDE_SCREEN = '(min-width: 768px)';

/** How many trend columns may carry a date, per width. */
const TICKS_WIDE = 14;
const TICKS_NARROW = 6;

/**
 * A window this long, running up to today, with nothing in it is more likely to
 * be a clinic that has never issued than a quiet fortnight, so the empty state
 * leads with "record an issue" instead of "try a wider range". The API has no
 * "has this clinic ever issued?" question and inventing one for a single empty
 * state would be a round trip to tell the user something the window already
 * implies.
 *
 * Both halves are load-bearing. A closed window in the past implies nothing
 * about whether the clinic issues today — an established dispensary looking at
 * last spring would be told it had never started, under a button offering to
 * take it to the issue screen — so length alone is not the test.
 */
const LIKELY_NEVER_ISSUED_DAYS = 90;

/** How a panel occupies the two-column grid the panels are laid out in. */
interface PanelLayout {
  readonly width: 'full' | 'half';
  /**
   * What draws this panel. Returning `null` is a legitimate answer — see
   * `panelLayout` — but it has to be written down.
   */
  readonly render: () => React.ReactNode;
}

export function IssuingAnalyticsPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sheetOpen, setSheetOpen] = useState(false);
  const isWide = useMediaQuery(WIDE_SCREEN);

  // Read the whole view out of the URL on every render rather than seeding state
  // from it once: there is then no second copy to fall behind a back button, a
  // pasted link, or a chip somewhere else on the page.
  const view = readView(searchParams);
  const range = resolveWindow(view, todayLocalIso());

  const update = (patch: Partial<IssuingView>) =>
    setSearchParams(writeView({ ...view, ...patch }), { replace: true });

  // The selected medicine's name. Asked for directly rather than read off the
  // ranked panel: a medicine with no issuing in the window has no member there,
  // and "no issuing for ⟨blank⟩" is the one empty state that must name its
  // subject.
  const { data: selectedItem } = useItemDetail(view.itemId);
  const selectedItemLabel = selectedItem?.brandName ?? null;

  const itemIds = view.itemId ? [view.itemId] : [];
  const panelsQuery = useIssuingAnalytics({
    from: range.from,
    to: range.to,
    bucket: range.bucket,
    measure: view.measure,
    itemTypes: view.itemTypes,
    itemIds,
  });

  const panels = panelsQuery.data;
  const totals = panels?.panels.totals;
  const measureMeta = measureMetaOf(totals, view.measure) ?? MEASURES[view.measure];
  const measureLabel = t(measureMeta.labelKey);
  const formatValue = (value: number) => formatMeasure(value, measureMeta.format, i18n.language);
  const formatPercent = (share: number) => formatShare(share, i18n.language);

  // Nothing issued means every measure is zero — checking only the active one
  // would call a period empty while the other measure had rows in it.
  //
  // A payload with no `totals` panel at all is a different thing from one whose
  // totals are zero, and here the difference matters: everywhere else in this
  // page a missing panel degrades to nothing drawn, which is a blank chart, but
  // this branch makes a claim *about the clinic*. So it is only made when the
  // panel that supports it arrived; without it the page draws the panels that
  // did.
  const anythingIssued = MEASURE_IDS.some((id) => sumMeasure(totals?.rows, id) > 0);
  const isEmpty = panelsQuery.isSuccess && totals !== undefined && !anythingIssued;

  const controls = { view, window: range, onChange: update };
  const panelProps = {
    panels,
    measure: view.measure,
    formatValue,
    formatPercent,
    isLoading: panelsQuery.isLoading,
  };

  function selectType(type: ItemType) {
    const only = view.itemTypes.length === 1 && view.itemTypes[0] === type;
    update({ itemTypes: only ? [] : [type] });
  }

  /**
   * Where each panel goes, and what draws it.
   *
   * The map is the point, not the layout it encodes. The server computes every
   * panel in `PANEL_IDS` on every request, so a panel added to the contract is
   * aggregated, serialised and sent whether or not anything here renders it —
   * and until this existed, the page read the four panels it knew by name, so a
   * fifth would have arrived, cost a query per page load, and silently never
   * appeared. Nothing would have failed. That is the failure mode this closes:
   * the page paying for work nobody sees.
   *
   * Annotated `Record<PanelId, PanelLayout>` and iterated over `PANEL_IDS`, so a
   * new panel is a compile error here until someone has said how wide it is and
   * what draws it. `render: () => null` is an allowed answer, because a panel
   * deliberately not drawn yet is a decision; an undrawn panel nobody noticed is
   * the bug.
   */
  const panelLayout: Record<PanelId, PanelLayout> = {
    totals: {
      width: 'full',
      render: () => (
        <HeadlineTiles
          panels={panels}
          measure={view.measure}
          isLoading={panelsQuery.isLoading}
          days={range.days}
        />
      ),
    },
    trend: {
      width: 'full',
      render: () => (
        <TrendPanel
          {...panelProps}
          bucket={range.bucket}
          granularityLabel={t(GRANULARITY_LABEL_KEY[range.bucket])}
          measureLabel={measureLabel}
          from={calendarLabel(range.from, i18n.language)}
          to={calendarLabel(range.to, i18n.language)}
          maxTicks={isWide ? TICKS_WIDE : TICKS_NARROW}
        />
      ),
    },
    by_type: {
      width: 'half',
      render: () => (
        <ByTypePanel {...panelProps} selectedTypes={view.itemTypes} onSelectType={selectType} />
      ),
    },
    top_items: {
      width: 'half',
      render: () => (
        <TopItemsPanel
          {...panelProps}
          measureLabel={measureLabel}
          selectedItemId={view.itemId}
          onSelectItem={(itemId) => update({ itemId: itemId === view.itemId ? null : itemId })}
        />
      ),
    },
  };

  return (
    <div className="mx-auto w-full max-w-[1180px]">
      <header className="mb-5 flex flex-col gap-1.5 sm:mb-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] sm:text-[26px]">
          {t('analytics.title')}
        </h1>
        <p className="text-[13.5px] text-muted">{t('analytics.subtitle')}</p>
      </header>

      <div className="mb-4 flex flex-col gap-3 sm:mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <PeriodChips {...controls} />
          {!isWide && (
            <Button variant="ghost" size="sm" onClick={() => setSheetOpen(true)}>
              <Icon name="filter" size={14} /> {t('analytics.filters')}
            </Button>
          )}
        </div>
        {isWide && <IssuingFilterFields {...controls} selectedItemLabel={selectedItemLabel} />}
      </div>

      {!isWide && sheetOpen && (
        <Modal
          title={t('analytics.filters')}
          onClose={() => setSheetOpen(false)}
          footer={
            <Button className="flex-1" onClick={() => setSheetOpen(false)}>
              {t('analytics.filtersDone')}
            </Button>
          }
        >
          <IssuingFilterFields {...controls} selectedItemLabel={selectedItemLabel} />
        </Modal>
      )}

      {view.itemId !== null && (
        <NarrowedBanner
          name={selectedItemLabel ?? t('analytics.medicineUnknown')}
          onClear={() => update({ itemId: null })}
        />
      )}

      {panelsQuery.isError ? (
        <ErrorState error={panelsQuery.error} onRetry={() => panelsQuery.refetch()} />
      ) : isEmpty ? (
        <EmptyState
          view={view}
          from={calendarLabel(range.from, i18n.language)}
          to={calendarLabel(range.to, i18n.language)}
          days={range.days}
          includesToday={range.includesToday}
          medicineName={selectedItemLabel}
          onWiden={() => update({ period: 'last90' })}
          onClearMedicine={() => update({ itemId: null })}
          onClearFilters={() => update({ itemTypes: [], itemId: null })}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 lg:grid-cols-2">
            {PANEL_IDS.map((id) => {
              const slot = panelLayout[id];
              const drawn = slot.render();
              if (drawn === null) return null;
              return (
                <div
                  key={id}
                  // Named so a test can assert that every panel the contract
                  // declares actually reached the page — the compile-time half
                  // of that guarantee is `panelLayout`'s annotation, and this is
                  // the half that catches a `render` returning nothing.
                  data-testid={`panel-${id}`}
                  className={cn('min-w-0', slot.width === 'full' && 'lg:col-span-2')}
                >
                  {drawn}
                </div>
              );
            })}
          </div>

          <IssueLogPanel
            from={range.from}
            to={range.to}
            itemTypes={view.itemTypes}
            itemIds={itemIds}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The narrowed-to-one-medicine state, stated in words above the panels.
 *
 * Redundant with the picker's token on a wide screen, and deliberately so: on a
 * narrow one the picker is behind the filter sheet, and every figure on the page
 * silently meaning one medicine is the worst thing this page could do.
 */
function NarrowedBanner({ name, onClear }: { name: string; onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-ink bg-paper-2 px-3 py-2">
      <Icon name="pill" size={15} className="shrink-0 text-muted" />
      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
        {t('analytics.medicineSelected', { name })}
      </span>
      <Button variant="ghost" size="sm" onClick={onClear}>
        <Icon name="x" size={13} strokeWidth={2.2} /> {t('analytics.medicineClear')}
      </Button>
    </div>
  );
}

interface EmptyStateProps {
  view: IssuingView;
  from: string;
  to: string;
  days: number;
  /** Whether the window runs up to the present day — see `LIKELY_NEVER_ISSUED_DAYS`. */
  includesToday: boolean;
  /** `null` while the selected medicine's name has not resolved. */
  medicineName: string | null;
  onWiden: () => void;
  onClearMedicine: () => void;
  onClearFilters: () => void;
}

/**
 * Four different nothings, because they call for four different next moves: a
 * medicine that wasn't issued, a filter combination with no rows, a quiet
 * period, and a clinic that hasn't started.
 */
function EmptyState({
  view,
  from,
  to,
  days,
  includesToday,
  medicineName,
  onWiden,
  onClearMedicine,
  onClearFilters,
}: EmptyStateProps) {
  const { t } = useTranslation();

  if (view.itemId !== null) {
    return (
      <EmptyCard
        icon="pill"
        // A title of its own rather than the shared "Selected medicine"
        // placeholder: this heading exists to name its subject, and "No issuing
        // for Selected medicine" names nothing while reading like a name.
        title={
          medicineName === null
            ? t('analytics.empty.medicineTitleUnknown')
            : t('analytics.empty.medicineTitle', { name: medicineName })
        }
        body={t('analytics.empty.medicineBody', { from, to })}
        action={
          <Button variant="ghost" onClick={onClearMedicine}>
            {t('analytics.empty.medicineAction')}
          </Button>
        }
      />
    );
  }

  if (view.itemTypes.length > 0) {
    return (
      <EmptyCard
        icon="filter"
        title={t('analytics.empty.filteredTitle')}
        body={t('analytics.empty.filteredBody')}
        action={
          <Button variant="ghost" onClick={onClearFilters}>
            {t('analytics.empty.filteredAction')}
          </Button>
        }
      />
    );
  }

  if (days >= LIKELY_NEVER_ISSUED_DAYS && includesToday) {
    return (
      <EmptyCard
        icon="box_out"
        title={t('analytics.empty.neverTitle')}
        body={t('analytics.empty.neverBody')}
        action={
          <Button asChild>
            <Link to="/issue">
              <Icon name="box_out" size={16} /> {t('analytics.empty.neverAction')}
            </Link>
          </Button>
        }
      />
    );
  }

  return (
    <EmptyCard
      icon="calendar"
      title={t('analytics.empty.rangeTitle')}
      body={t('analytics.empty.rangeBody', { from, to })}
      action={
        <Button variant="ghost" onClick={onWiden}>
          {t('analytics.empty.rangeAction')}
        </Button>
      }
    />
  );
}

function EmptyCard({
  icon,
  title,
  body,
  action,
}: {
  icon: string;
  title: string;
  body: string;
  action: React.ReactNode;
}) {
  return (
    <Card className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
        <Icon name={icon} size={22} />
      </div>
      <div className="text-[14px] font-semibold tracking-[-0.01em]">{title}</div>
      <p className="max-w-[340px] text-[12.5px] text-muted">{body}</p>
      {action}
    </Card>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation();
  const message =
    error instanceof ApiError ? translateApiError(error) : t('analytics.couldNotLoadMsg');
  return (
    <Card className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive-bg text-destructive">
        <Icon name="alert" size={22} />
      </div>
      <div className="text-[14px] font-semibold tracking-[-0.01em]">
        {t('analytics.couldNotLoad')}
      </div>
      <p className="max-w-[340px] text-[12.5px] text-muted">{message}</p>
      <Button onClick={onRetry}>{t('common.tryAgain')}</Button>
    </Card>
  );
}
